import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiBars3, HiXMark } from 'react-icons/hi2';

import PATHS from '../../../../Routers/PATHS';
import checkLang from '../../../../helpers/checkLang';
import { NAV_LINKS } from '../../constants';
import { Button } from '../../../shared/ui';
import { Logo } from '../../../shared/components';

/**
 * "العربية | English" — one control showing both languages, with the active one
 * painted in the brand blue. That is how the design marks the current
 * choice, rather than offering only the language you are not reading.
 */
const LanguageSwitch = ({ isArabic, onSwitch, className = '' }) => {
    const activeClasses = 'tw-text-v2-brand focus:tw-text-v2-brand active:tw-text-v2-brand';
    const idleClasses = 'hover:tw-text-v2-brand focus:tw-text-v2-brand tw-transition-colors';

    return (
        <p className={`tw-flex tw-items-center tw-gap-1.5 tw-text-v2-sm tw-text-v2-text-muted ${className}`}>
            <button
                type="button"
                lang="ar"
                onClick={() => onSwitch('ar')}
                aria-current={isArabic ? 'true' : undefined}
                className={isArabic ? activeClasses : idleClasses}
            >
                العربية
            </button>
            <span aria-hidden>|</span>
            <button
                type="button"
                lang="en"
                onClick={() => onSwitch('en')}
                aria-current={!isArabic ? 'true' : undefined}
                className={!isArabic ? activeClasses : idleClasses}
            >
                English
            </button>
        </p>
    );
};

/**
 * Header for the redesigned landing pages.
 *
 * From Figma node 16097:50622: an 83px white bar, 48px of side padding, the
 * links centred with 42px between them, and the account button and language
 * switch at the far side. The bar spans the full width — it is not inside the
 * 1536px content container the sections use.
 *
 * Every signed-out page but the auth screens gets this bar — see
 * `isV2AuthRoute` in `shared/routes.js`.
 *
 * Active state is computed from the pathname rather than handed to NavLink:
 * on react-router v5 `activeClassName` and a Tailwind colour class carry equal
 * specificity, so which one wins depends on stylesheet order rather than on
 * which link is active.
 */
const NavBar = () => {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const user = useSelector(state => state.auth?.user);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const isArabic = i18n.language?.startsWith('ar');

    // Home would otherwise match every route, since every path starts with "/".
    const isActive = (to) =>
        to === PATHS.LANDING_HOME.URL ? pathname === to : pathname.startsWith(to);

    // checkLang also swaps the RTL/LTR stylesheets and stores the preference;
    // switching the language here by hand would only get half of that right.
    const switchTo = (language) => { if (language !== i18n.language) checkLang(language); };

    // Leaving the menu open across a navigation would cover the new page.
    useEffect(() => { setIsMenuOpen(false); }, [pathname]);

    const account = user
        ? { to: PATHS.USER_DASHBOARD.URL, label: t('v2.nav.dashboard') }
        : { to: PATHS.LANDING_SIGNIN.URL, label: t('v2.nav.signIn') };

    // The active link is lettered in the brand blue and set bold. It was a
    // gradient clipped to the glyphs until the repaint made every brand fill
    // flat.
    //
    // The `focus:` and `active:` colours are not decoration — they are the fix
    // for a global rule. public/css/rsuite.min.css, loaded from index.html,
    // carries:
    //
    //     a:focus, a:hover { color: var(--rs-text-link-hover) }
    //     a:active        { color: var(--rs-text-link-active) }
    //
    // Those selectors score (0,1,1); a plain Tailwind colour utility scores
    // (0,1,0) and loses. So a click leaves the link focused and rsuite repaints
    // it rsuite's own blue, which is not the brand's. `hover:` never showed the
    // bug, because its compiled selector carries a pseudo-class of its own and
    // already outranks them.
    // Repeating the colour under `focus:`/`active:` puts the module's own
    // classes back on top without reaching for `!important`.
    const linkClasses = (to) =>
        isActive(to)
            ? 'tw-text-v2-brand focus:tw-text-v2-brand active:tw-text-v2-brand tw-font-bold'
            : 'tw-text-v2-text-muted hover:tw-text-v2-brand focus:tw-text-v2-brand active:tw-text-v2-brand tw-transition-colors';

    // Clicking also leaves the browser's default outline behind — a black box
    // that reads as a stray border on the bar. Swapped for the brand ring the
    // buttons already use, and drawn only for keyboard focus.
    const focusRing =
        'tw-rounded focus-visible:tw-outline-none focus-visible:tw-ring-2 ' +
        'focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-2';

    return (
        <header className="tw-sticky tw-top-0 tw-z-50 tw-bg-white/95 tw-backdrop-blur">
            <div className="tw-relative tw-flex tw-h-[83px] tw-items-center tw-justify-between tw-gap-6 tw-px-5 md:tw-px-12">
                {/* The logo opens the line — the right in Arabic, as designed. */}
                <Logo />

                {/* Centred on the bar itself, not between its neighbours, so the
                    links stay put as the account label changes width. */}
                <nav
                    aria-label={t('v2.nav.primary')}
                    className="tw-absolute tw-start-1/2 tw--translate-x-1/2 rtl:tw-translate-x-1/2 tw-hidden lg:tw-flex tw-items-center tw-gap-[42px] tw-text-v2-label"
                >
                    {NAV_LINKS.map(link => (
                        <Link
                            key={link.key}
                            to={link.to}
                            aria-current={isActive(link.to) ? 'page' : undefined}
                            className={`${linkClasses(link.to)} ${focusRing}`}
                        >
                            {t(link.labelKey)}
                        </Link>
                    ))}
                </nav>

                {/* Language, account and the small-screen menu close the line —
                    the account button sits at the far end of the bar with the
                    language switch just inside it, so DOM order is language then
                    account and the flex direction mirrors it in RTL.
                    The first two are desktop-only: logo, account, language and a
                    burger together need about 450px, which overflows a phone.
                    Below `lg` they move into the menu instead. */}
                <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-hidden lg:tw-block">
                        <LanguageSwitch isArabic={isArabic} onSwitch={switchTo} />
                    </div>

                    <div className="tw-hidden lg:tw-block">
                        <Button to={account.to} size="sm">{account.label}</Button>
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsMenuOpen(open => !open)}
                        aria-expanded={isMenuOpen}
                        aria-controls="v2-mobile-menu"
                        aria-label={t(isMenuOpen ? 'v2.nav.closeMenu' : 'v2.nav.openMenu')}
                        className="lg:tw-hidden tw-p-2 tw-text-v2-text-heading"
                    >
                        {isMenuOpen ? <HiXMark size={24} /> : <HiBars3 size={24} />}
                    </button>
                </div>
            </div>

            {/* Rebuilt on open rather than kept mounted, so a stale menu can never
                sit invisibly over the page after a route change. */}
            {isMenuOpen && (
                <nav
                    id="v2-mobile-menu"
                    aria-label={t('v2.nav.primary')}
                    className="lg:tw-hidden tw-border-t tw-border-v2-ground-500 tw-bg-white"
                >
                    <div className="tw-flex tw-flex-col tw-px-5 tw-py-2 md:tw-px-12">
                        {NAV_LINKS.map(link => (
                            <Link
                                key={link.key}
                                to={link.to}
                                aria-current={isActive(link.to) ? 'page' : undefined}
                                className={`tw-py-3 tw-text-v2-label ${linkClasses(link.to)} ${focusRing}`}
                            >
                                {t(link.labelKey)}
                            </Link>
                        ))}

                        {/* The two controls the bar drops on a phone. */}
                        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-t tw-border-v2-ground-500 tw-pt-4 tw-pb-2">
                            <Button to={account.to} size="sm">{account.label}</Button>
                            <LanguageSwitch isArabic={isArabic} onSwitch={switchTo} />
                        </div>
                    </div>
                </nav>
            )}
        </header>
    );
};

export default NavBar;
